import { Col, Row } from "react-bootstrap";
import { CompanyProfileData } from "../Models/CompanyProfileData";
import { CompanyTermsTypes } from "../Models/CompanyTerms";
import { InvestorProfileData } from "../Models/InvestorProfileData";
import { PitchFormData } from "../Models/PitchFormData";
import { TermsFormValues } from "../../types/PitchTypes/PitchTypes";
import { User } from "../../types/Types";
import { ensureHttpUrlPrefix } from "../../utils/ensureHttpUrlPrefix";

export function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = String(value).trim();
  return text === "" ? "—" : text;
}

export function formatYesNo(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (value === 1 || value === true) return "Yes";
  if (value === 0 || value === false) return "No";
  const s = String(value).toLowerCase();
  if (s === "1" || s === "true" || s === "yes" || s === "on") return "Yes";
  if (s === "0" || s === "false" || s === "no" || s === "off") return "No";
  return formatDetailValue(value);
}

type DetailField = {
  label: string;
  value: unknown;
  yesNo?: boolean;
  isLink?: boolean;
};

function parseCertificationPdfLinks(json: string | undefined | null): DetailField[] {
  if (!json || !String(json).trim()) return [];
  try {
    const entries = JSON.parse(String(json)) as Array<{
      fileName?: string;
      url?: string;
    }>;
    if (!Array.isArray(entries)) return [];
    return entries
      .filter((e) => e?.url)
      .map((e, index) => ({
        label: e.fileName || `Certification PDF ${index + 1}`,
        value: e.url,
        isLink: true,
      }));
  } catch {
    return [{ label: "Certification PDFs (JSON)", value: json }];
  }
}

export function AdminDetailSection({
  title,
  fields,
  loading,
}: {
  title: string;
  fields: DetailField[];
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="card shadow-sm border-0 mb-3 p-3">
        <h5 className="fw-semibold mb-3">{title}</h5>
        <p className="text-muted small mb-0">Loading…</p>
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0 mb-3 p-3">
      <h5 className="fw-semibold mb-3">{title}</h5>
      <Row>
        {fields.map(({ label, value, yesNo, isLink }) => {
          const text = yesNo ? formatYesNo(value) : formatDetailValue(value);
          const href =
            isLink && value != null && String(value).trim() !== ""
              ? ensureHttpUrlPrefix(String(value))
              : null;

          return (
            <Col md={6} className="mb-3" key={label}>
              <strong>{label}</strong>
              <div className="text-muted small text-break">
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    Download PDF
                  </a>
                ) : (
                  text
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

export function buildPortalUserFields(user: User | undefined): DetailField[] {
  if (!user) {
    return [{ label: "Member", value: "—" }];
  }
  return [
    { label: "User ID", value: user.id },
    { label: "Object ID", value: user.objectId },
    { label: "First name", value: user.firstName },
    { label: "Last name", value: user.lastName },
    { label: "Email", value: user.email },
    { label: "Phone", value: user.phoneNumber },
    { label: "Membership", value: user.membershipName },
    { label: "Business name", value: user.businessName },
    { label: "City", value: user.city },
    { label: "Country", value: user.country },
  ];
}

export function buildCompanyProfileFields(
  company: CompanyProfileData | null | undefined
): DetailField[] {
  if (!company) return [];
  return [
    { label: "Profile ID", value: company.id },
    { label: "User ID", value: company.userId },
    { label: "Status", value: company.status },
    { label: "Profile type", value: company.profileType },
    { label: "Is completed", value: company.isCompleted },
    { label: "Match ID", value: company.matchId },
    { label: "Company name", value: company.companyName },
    { label: "Company registration no.", value: company.companyRegistrationNumber },
    { label: "Country of registration", value: company.countryOfRegistration },
    { label: "Description", value: company.companyDescription },
    { label: "Mission statement", value: company.missionStatement },
    { label: "Vision", value: company.vision },
    { label: "Development stage", value: company.developmentStage },
    { label: "Sectors of interest", value: company.sectorsOfInterest },
    { label: "Product / service", value: company.productServiceDescription },
    { label: "Market differentiation", value: company.productMarketDifferentiation },
    { label: "Target market", value: company.targetMarket },
    { label: "Landscape", value: company.landscape },
    { label: "Amount sought", value: company.amountSought },
    { label: "Use of funds", value: company.useOfFunds },
    { label: "Revenue projections", value: company.revenueProjections },
    { label: "Profit margins", value: company.profitMargins },
    { label: "Expenses", value: company.expenses },
  ];
}

export function buildCompanyTermsFields(terms: CompanyTermsTypes): DetailField[] {
  return [
    { label: "Terms ID", value: terms.id },
    { label: "User ID", value: terms.userId },
    { label: "Contact name", value: terms.contactName },
    { label: "Position", value: terms.position },
    { label: "Company", value: terms.company },
    { label: "Registered address", value: terms.registeredAddress },
    { label: "Invoice address", value: terms.invoiceAddress },
    { label: "Telephone", value: terms.telephoneNumber },
    { label: "Email address", value: terms.emailAddress },
    { label: "Company no.", value: terms.companyNo },
    { label: "Date of registration", value: terms.dateOfRegistration },
    { label: "VAT registration no.", value: terms.vatRegistrationNo },
    { label: "Definition of company", value: terms.definitionCompany },
    { label: "Raise amount", value: terms.raiseAmount },
    { label: "Signatory name", value: terms.name },
    { label: "Signed", value: terms.signed },
    { label: "Signed position", value: terms.signedPosition },
    { label: "Signed date", value: terms.signedDate },
    { label: "Investor amount", value: terms.investorAmount },
    { label: "Investor name", value: terms.investorName },
    { label: "Timestamp", value: terms.timeStamp },
    ...parseCertificationPdfLinks(terms.certificationPdfsJson),
  ];
}

export function buildPitchFields(pitch: PitchFormData | null | undefined): DetailField[] {
  if (!pitch) return [];
  return [
    { label: "Pitch ID", value: pitch.id },
    { label: "Status", value: pitch.status },
    { label: "Name", value: pitch.name },
    { label: "Created by (object ID)", value: pitch.createdByObjectId },
    { label: "User full name", value: pitch.userFullName },
    { label: "User email", value: pitch.userEmail },
    { label: "Company name", value: pitch.companyName },
    { label: "Country of registration", value: pitch.countryOfRegistration },
    { label: "Company registration no.", value: pitch.companyRegistrationNumber },
    { label: "Description", value: pitch.companyDescription },
    { label: "Mission", value: pitch.mission },
    { label: "Vision", value: pitch.vision },
    { label: "Sector", value: pitch.sector },
    { label: "Region", value: pitch.region },
    { label: "Investment type", value: pitch.investmentType },
    { label: "Investment ticket size", value: pitch.investmentTicketSize },
    { label: "Amount sought", value: pitch.amountSought },
    { label: "Market problem", value: pitch.identifiedMarketProblem },
    { label: "Solution", value: pitch.solution },
    { label: "Market size", value: pitch.marketSize },
    { label: "Growth potential", value: pitch.growthPotential },
    { label: "Target audience", value: pitch.targetAudience },
    { label: "Revenue streams", value: pitch.revenueStreams },
    { label: "Pricing strategy", value: pitch.pricingStrategy },
    { label: "Revenue forecast", value: pitch.revenueForecast },
    { label: "Profit margins", value: pitch.profitMargins },
    { label: "Break-even margins", value: pitch.breakEvenMargins },
    { label: "Use of funds", value: pitch.useOfFunds },
  ];
}

export function buildInvestorProfileFields(
  investor: InvestorProfileData | null | undefined
): DetailField[] {
  if (!investor) return [];
  return [
    { label: "Profile ID", value: investor.id },
    { label: "User ID", value: investor.userId },
    { label: "Status", value: investor.status },
    { label: "Profile type", value: investor.profileType },
    { label: "Is completed", value: investor.isCompleted },
    { label: "Match ID", value: investor.matchId },
    { label: "Investment type", value: investor.investmentType },
    { label: "Sectors of interest", value: investor.sectorsOfInterest },
    { label: "Development stage", value: investor.developmentStage },
    { label: "Frequency of updates", value: investor.frequencyOfUpdates },
    { label: "Presentation preferences", value: investor.presentationPreferences },
    { label: "Investment time horizon", value: investor.investmentTimeHorizon },
    { label: "Number of previous investments", value: investor.numberOfPreviousInvestments },
    { label: "Investment ticket size", value: investor.investmentTicketSize },
    {
      label: "Experience with EIS/SEIS",
      value: formatYesNo(investor.isExperienceWithEISorSEIS),
    },
    {
      label: "Tax breaks important",
      value: formatYesNo(investor.isTaxBreaksImportant),
    },
    {
      label: "Requires third-party review",
      value: formatYesNo(investor.isRequireThirdPartyReview),
    },
    {
      label: "Using third party for transactions",
      value: formatYesNo(investor.isUsingThirdPartyForTransactions),
    },
  ];
}

const INVESTOR_TERMS_LABELS: Record<keyof TermsFormValues, string> = {
  id: "Terms record ID",
  userId: "User ID",
  companyProfileId: "Company profile ID",
  investorProfileId: "Investor profile ID",
  dateAccepted: "Date accepted",
  isAccepted: "Is accepted",
  riskWarning: "Risk warning",
  fsmaCertification: "FSMA certification",
  fsmaCertificationInvestorName: "FSMA investor name",
  fsmaCertificationAddress: "FSMA address",
  fsmaCertificationSignature: "FSMA signature",
  fsmaCertificationDate: "FSMA date",
  fsmaCertificationScheduleOne: "FSMA schedule one",
  partASignature: "Part A signature",
  partADate: "Part A date",
  partAName: "Part A name",
  partAAddress: "Part A address",
  partAAccepted: "Part A accepted",
  partBSignature: "Part B signature",
  partBDate: "Part B date",
  partBName: "Part B name",
  partBAddress: "Part B address",
  partBAccepted: "Part B accepted",
  partCName: "Part C name",
  partCAddress: "Part C address",
  partCSignature: "Part C signature",
  partCDate: "Part C date",
  partCFinancialConduct: "Part C financial conduct",
  partCInvestorExperience: "Part C investor experience",
  partCInvestorUnderstanding: "Part C investor understanding",
  partCpartTwoName: "Part C (II) name",
  partCpartTwoAddress: "Part C (II) address",
  partCpartTwoSignature: "Part C (II) signature",
  partCpartTwoOrgName: "Part C (II) organisation",
  partCpartTwoContact: "Part C (II) contact",
  timestamp: "Timestamp",
  ongoingObligation: "Ongoing obligation",
  noAdviceConfirmation: "No advice confirmation",
  investmentType: "Investment type (terms)",
  finalAccept: "Final accept",
  certificationPdfsJson: "Certification PDFs",
};

/** Checkbox / acceptance fields stored as 0, 1, true, false, or "on". */
const INVESTOR_TERMS_YES_NO_KEYS = new Set<keyof TermsFormValues>([
  "isAccepted",
  "riskWarning",
  "fsmaCertification",
  "fsmaCertificationScheduleOne",
  "partAAccepted",
  "partBAccepted",
  "partCFinancialConduct",
  "partCInvestorExperience",
  "partCInvestorUnderstanding",
  "ongoingObligation",
  "noAdviceConfirmation",
  "finalAccept",
]);

export function buildInvestorTermsFields(terms: TermsFormValues): DetailField[] {
  const fields = (
    Object.keys(INVESTOR_TERMS_LABELS) as (keyof TermsFormValues)[]
  )
    .filter((key) => key !== "certificationPdfsJson")
    .map((key) => ({
      label: INVESTOR_TERMS_LABELS[key],
      value: terms[key],
      yesNo: INVESTOR_TERMS_YES_NO_KEYS.has(key),
    }));

  return [...fields, ...parseCertificationPdfLinks(terms.certificationPdfsJson)];
}

export function CompanyProfileFilesSection({
  files,
}: {
  files: Array<{ id?: number; media?: string }> | undefined;
}) {
  if (!files?.length) {
    return (
      <div className="card shadow-sm border-0 mb-3 p-3">
        <h5 className="fw-semibold mb-3">Company profile files</h5>
        <p className="text-muted small mb-0">No files uploaded</p>
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0 mb-3 p-3">
      <h5 className="fw-semibold mb-3">Company profile files</h5>
      <ul className="list-unstyled mb-0">
        {files.map((file, index) => (
          <li key={file.id ?? index} className="mb-2">
            {file.media ? (
              <a
                href={ensureHttpUrlPrefix(file.media)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {file.media}
              </a>
            ) : (
              "—"
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
